import { useEffect, useState, type CSSProperties } from 'react'
import { AnimatePresence, motion, Transition, Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'

const DEFAULT_INTERVAL_MS = 2000

const loadingTextVariants: Variants = {
  initial: { opacity: 0, filter: 'blur(4px)' },
  animate: { opacity: 1, filter: 'blur(0px)' },
  exit: { opacity: 0, filter: 'blur(4px)' },
}

const loadingTextTransition: Transition = { duration: 0.3 }

interface LoadingStateTextProps {
  messages: Array<string>
  interval?: number
  shimmer?: boolean
  className?: string
}

function useMessageCycler(messages: Array<string>, interval: number) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (messages.length <= 1) return

    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % messages.length)
    }, interval)

    return () => clearInterval(timer)
  }, [messages, interval])

  // Wrap on read too: a shorter `messages` can arrive while index sits past its end.
  return messages[index % messages.length]
}

const shimmerStyle: CSSProperties = {
  backgroundImage:
    'linear-gradient(90deg, oklch(from var(--foreground) l c h / 0.5) 0%, oklch(from var(--foreground) l c h / 0.5) 40%, oklch(from var(--foreground) l c h / 0.8) 50%, oklch(from var(--foreground) l c h / 0.5) 60%, oklch(from var(--foreground) l c h / 0.5) 100%)',
  backgroundSize: '200% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  animation: 'shimmer 1700ms ease-in-out infinite forwards',
}

function LoadingStateText({
  messages,
  interval = DEFAULT_INTERVAL_MS,
  shimmer = true,
  className,
}: LoadingStateTextProps) {
  const currentMessage = useMessageCycler(messages, interval)

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={currentMessage}
        variants={loadingTextVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={loadingTextTransition}
        className={twMerge('text-foreground-placeholder inline-block', className)}
        style={shimmer ? shimmerStyle : undefined}
      >
        {currentMessage}
      </motion.span>
    </AnimatePresence>
  )
}

export { LoadingStateText }
